// App Fleet — which version every phone is really running, whether it can
// receive the latest update, and when to force a store update.
//
// The RN app ships OTAs through EAS Update with FINGERPRINT runtime versions:
// an update only reaches binaries whose native fingerprint matches. A phone's
// state is therefore per platform + channel + runtime, and the server decides
// it (CITS-Server src/utils/appFleet.js -> device.status); this page displays
// it. Versions are shown as separate parts — "Store 4.1.0 (40) · Update 7"
// plus the runtime fingerprint — because the shared OTA counter says nothing
// about reachability. Minimum versions are STORE minimums (CITS-Server
// utils/appVersions.js decideUpdate); OTAs are forced by their own level.

import { useCallback, useEffect, useMemo, useState } from 'react'
import api from '../api/axios'
import {
  ArrowPathIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline'
import DeviceDrawer from './appFleet/DeviceDrawer'
import Fingerprint from './appFleet/Fingerprint'
import {
  type Device,
  type HealthItem,
  type NativeBuild,
  type OtaRelease,
  type Platform,
  type StatusKey,
  STATUS_ORDER,
  SEGMENT_COLOR,
  STATUS_STYLE,
  buildLabel,
  buildsForRuntime,
  channelOf,
  compareVersions,
  daysSince,
  deviceName,
  formatWhen,
  isActive,
  isCounted,
  otaLabel,
  platformLabel,
  storeLabel,
  timeAgo,
  updateName,
  versionLine,
} from './appFleet/fleet'

type Tab = 'devices' | 'releases' | 'builds' | 'policy'
type StatusFilter = 'all' | 'latest' | 'available' | 'no_updates' | 'store' | 'unrecorded' | 'unknown'

interface PolicyForm {
  force_update_enabled: boolean
  min_ios_version: string
  min_android_version: string
  min_pwa_version: string
  update_message: string
  ios_store_url: string
  android_store_url: string
}

const EMPTY_POLICY: PolicyForm = {
  force_update_enabled: false,
  min_ios_version: '',
  min_android_version: '',
  min_pwa_version: '',
  update_message: '',
  ios_store_url: '',
  android_store_url: '',
}

const isVersion = (v: string) => /^\d+\.\d+\.\d+$/.test(v.trim())

function errMsg(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { error?: string; message?: string } } }
  return e?.response?.data?.error || e?.response?.data?.message || fallback
}

function csvCell(v: unknown) {
  const s = v == null ? '' : String(v)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

const LEVEL_STYLE: Record<string, string> = {
  critical: 'bg-red-50 text-red-700',
  soon: 'bg-amber-50 text-amber-700',
  normal: 'bg-slate-100 text-slate-600',
}

export default function AppVersionControl() {
  const [tab, setTab] = useState<Tab>('devices')
  const [devices, setDevices] = useState<Device[]>([])
  const [retiredCount, setRetiredCount] = useState(0)
  const [showRetired, setShowRetired] = useState(false)
  const [health, setHealth] = useState<HealthItem[]>([])
  const [latest, setLatest] = useState<OtaRelease[]>([])
  const [ota, setOta] = useState<OtaRelease[]>([])
  const [builds, setBuilds] = useState<NativeBuild[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [selected, setSelected] = useState<Device | null>(null)
  const [toast, setToast] = useState<{ msg: string; tone: 'ok' | 'error' } | null>(null)

  const notify = useCallback((msg: string, tone: 'ok' | 'error' = 'ok') => {
    setToast({ msg, tone })
    window.setTimeout(() => setToast(null), 3000)
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      const [d, r] = await Promise.all([
        api.get('/super-admin/devices', { params: showRetired ? { includeRetired: 1 } : {} }),
        api.get('/super-admin/devices/releases').catch(() => ({ data: { ota: [], builds: [] } })),
      ])
      setDevices(d.data.devices || [])
      setRetiredCount(d.data.retiredCount || 0)
      setHealth(d.data.health || [])
      setLatest(d.data.latest || [])
      setOta(r.data.ota || [])
      setBuilds(r.data.builds || [])
    } catch (err) {
      setLoadError(errMsg(err, 'Failed to load devices'))
    } finally {
      setLoading(false)
    }
  }, [showRetired])

  useEffect(() => {
    load()
  }, [load])

  // Keep the open drawer in step with a reload (e.g. after retiring).
  useEffect(() => {
    if (selected) setSelected(devices.find((d) => d.id === selected.id) ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices])

  const liveCount = devices.filter(isCounted).length
  const releaseById = useMemo(() => new Map(ota.filter((r) => r.id != null).map((r) => [r.id as number, r])), [ota])

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900">App Fleet</h1>
            <p className="text-sm text-slate-500">
              Which build and update every phone runs, whether it can take the latest update, and when to force a store update.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => exportCsv(devices.filter((d) => !d.retired_at))}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50"
            >
              <ArrowDownTrayIcon className="w-4 h-4" /> Export CSV
            </button>
            <button
              onClick={load}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50"
            >
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>

        <FleetHealth items={health} loading={loading} />

        <div className="flex gap-1 border-b border-slate-200 mb-5 overflow-x-auto" role="tablist">
          {(
            [
              ['devices', 'Devices', liveCount],
              ['releases', 'Releases', ota.length],
              ['builds', 'Builds', builds.length],
              ['policy', 'Update policy', null],
            ] as const
          ).map(([key, label, count]) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`px-3 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap flex items-center gap-1.5 ${
                tab === key ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {label}
              {count != null && (
                <span className={`text-[11px] px-1.5 rounded-full ${tab === key ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {loadError && (
          <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" /> {loadError}
          </div>
        )}

        {tab === 'devices' && (
          <DevicesTab
            devices={devices}
            latest={latest}
            builds={builds}
            loading={loading}
            retiredCount={retiredCount}
            showRetired={showRetired}
            onShowRetired={setShowRetired}
            onOpen={setSelected}
          />
        )}
        {tab === 'releases' && <ReleasesTab ota={ota} builds={builds} devices={devices.filter((d) => !d.retired_at)} />}
        {tab === 'builds' && <BuildsTab builds={builds} />}
        {tab === 'policy' && <PolicyTab devices={devices.filter((d) => !d.retired_at)} notify={notify} />}
      </div>

      {selected && (
        <DeviceDrawer
          device={selected}
          release={selected.status.release_id != null ? releaseById.get(selected.status.release_id) ?? null : null}
          latestRelease={selected.status.latest_release_id != null ? releaseById.get(selected.status.latest_release_id) ?? null : null}
          builds={builds}
          onClose={() => setSelected(null)}
          onChanged={load}
          notify={notify}
        />
      )}

      {toast && (
        <div
          role="status"
          className={`fixed left-1/2 -translate-x-1/2 bottom-5 px-4 py-2 rounded-lg text-sm shadow-lg ${
            toast.tone === 'error' ? 'bg-red-600 text-white' : 'bg-slate-900 text-white'
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  )
}

/* ── Fleet health ────────────────────────────────────────────────────────── */

function FleetHealth({ items, loading }: { items: HealthItem[]; loading: boolean }) {
  const [open, setOpen] = useState<string | null>(null)
  if (loading && !items.length) return null
  if (!items.length) {
    return (
      <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-600">
        <CheckCircleIcon className="w-4 h-4 text-emerald-600" /> Fleet data is consistent: every update phones run is recorded and every release has a build.
      </div>
    )
  }
  const icon = (tone: HealthItem['tone']) =>
    tone === 'warn' ? (
      <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
    ) : tone === 'ok' ? (
      <CheckCircleIcon className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
    ) : (
      <InformationCircleIcon className="w-4 h-4 text-sky-600 flex-shrink-0 mt-0.5" />
    )
  return (
    <section aria-label="Fleet health" className="mb-4 rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
      {items.map((h) => (
        <div key={h.key} className="px-3 py-2.5 text-sm">
          <button className="w-full flex items-start gap-2 text-left" onClick={() => setOpen(open === h.key ? null : h.key)} aria-expanded={open === h.key}>
            {icon(h.tone)}
            <span className="flex-1">
              <span className="font-medium text-slate-900">{h.title}</span>
              <span className="block text-slate-500">{h.explanation}</span>
            </span>
            <span className="text-xs text-blue-700 whitespace-nowrap">{open === h.key ? 'Hide' : 'How to fix'}</span>
          </button>
          {open === h.key && (
            <div className="mt-2 ml-6 space-y-1.5 text-slate-700">
              <p>{h.fix}</p>
              {h.releases?.length ? (
                <ul className="text-xs space-y-1">
                  {h.releases.map((r, i) => (
                    <li key={i} className="flex flex-wrap items-center gap-2">
                      <span className="font-mono">{r.ota_build || r.ota_version ? updateName(r) : 'release'}</span>
                      <span>{platformLabel(r.platform)}</span>
                      <span className="text-slate-400">{r.channel}</span>
                      <Fingerprint value={r.runtime_version} className="text-slate-600" />
                    </li>
                  ))}
                </ul>
              ) : null}
              {h.update_ids?.length ? (
                <p className="text-xs text-slate-500">
                  Update ids: <span className="font-mono break-all">{h.update_ids.join(', ')}</span>
                </p>
              ) : null}
              {h.runtimes?.length ? (
                <ul className="text-xs space-y-1">
                  {h.runtimes.map((r, i) => (
                    <li key={i} className="flex items-center gap-2">
                      {platformLabel(r.platform)} <Fingerprint value={r.runtime} className="text-slate-600" />
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          )}
        </div>
      ))}
    </section>
  )
}

/* ── Devices ─────────────────────────────────────────────────────────────── */

const matchesFilter = (k: StatusKey, f: StatusFilter) =>
  f === 'all' || (f === 'available' ? k === 'behind' || k === 'embedded' : k === f)

function DevicesTab({
  devices,
  latest,
  builds,
  loading,
  retiredCount,
  showRetired,
  onShowRetired,
  onOpen,
}: {
  devices: Device[]
  latest: OtaRelease[]
  builds: NativeBuild[]
  loading: boolean
  retiredCount: number
  showRetired: boolean
  onShowRetired: (v: boolean) => void
  onOpen: (d: Device) => void
}) {
  const [q, setQ] = useState('')
  const [company, setCompany] = useState('all')
  const [platform, setPlatform] = useState<'all' | Platform>('all')
  const [channel, setChannel] = useState('all')
  const [active, setActive] = useState<'7' | '30' | 'all'>('30')
  const [status, setStatus] = useState<StatusFilter>('all')

  const activeDays = active === 'all' ? 'all' : Number(active)
  const live = devices.filter((d) => !d.retired_at)
  // Counts use the server's 30-day activity rule, whatever the filter shows.
  const counted = live.filter((d) => !d.inactive)
  const inactiveCount = live.length - counted.length

  const companies = useMemo(() => {
    const m = new Map<string, string>()
    for (const d of devices) m.set(d.company_code, d.company_name || d.company_code)
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [devices])
  const channels = useMemo(() => [...new Set(devices.map((d) => channelOf(d)))].sort(), [devices])

  const rows = devices.filter((d) => {
    if (!showRetired && d.retired_at) return false
    if (!d.retired_at && !isActive(d, activeDays)) return false
    if (company !== 'all' && d.company_code !== company) return false
    if (platform !== 'all' && d.platform !== platform) return false
    if (channel !== 'all' && channelOf(d) !== channel) return false
    if (!matchesFilter(d.status.key, status)) return false
    if (q) {
      const hay = `${deviceName(d)} ${d.employee_id ?? ''} ${d.company_name ?? ''} ${d.company_code} ${d.device_model ?? ''} ${d.runtime_version ?? ''}`.toLowerCase()
      if (!hay.includes(q.toLowerCase())) return false
    }
    return true
  })

  const count = (f: StatusFilter) => counted.filter((d) => matchesFilter(d.status.key, f)).length
  const current = count('latest') + count('no_updates')
  const pctCurrent = counted.length ? Math.round((current / counted.length) * 100) : 0

  const statCls = 'bg-white px-4 py-3 text-left hover:bg-slate-50'
  return (
    <>
      <div className="grid gap-3 md:grid-cols-2 mb-3">
        {(['ios', 'android'] as Platform[]).map((p) => (
          <PlatformCard
            key={p}
            platform={p}
            devices={counted.filter((d) => d.platform === p)}
            latest={latest.filter((r) => r.platform === p)}
            builds={builds}
          />
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden mb-5">
        <button className={statCls} onClick={() => setStatus('all')}>
          <b className="block font-mono text-xl text-slate-900">{counted.length}</b>
          <span className="text-xs text-slate-500">Active devices (30 days)</span>
        </button>
        <button className={statCls} onClick={() => setStatus('latest')}>
          <b className="block font-mono text-xl text-emerald-700">{pctCurrent}%</b>
          <span className="text-xs text-slate-500">Current for their build</span>
        </button>
        <button className={statCls} onClick={() => setStatus('available')}>
          <b className="block font-mono text-xl text-amber-700">{count('available')}</b>
          <span className="text-xs text-slate-500">Update available</span>
        </button>
        <button className={statCls} onClick={() => setStatus('store')}>
          <b className="block font-mono text-xl text-red-700">{count('store')}</b>
          <span className="text-xs text-slate-500">Below store minimum</span>
        </button>
        <button
          className={statCls}
          onClick={() => {
            setActive('all')
            setStatus('all')
          }}
        >
          <b className="block font-mono text-xl text-slate-900">{inactiveCount}</b>
          <span className="text-xs text-slate-500">Inactive (30+ days, not counted)</span>
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <label className="flex-1 min-w-[220px] flex items-center gap-2 px-3 rounded-lg border border-slate-300 bg-white">
          <MagnifyingGlassIcon className="w-4 h-4 text-slate-400" />
          <input
            id="fleet-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search employee, company, device or fingerprint"
            className="w-full py-2 text-sm outline-none bg-transparent"
          />
        </label>
        <select id="fleet-company" aria-label="Company" value={company} onChange={(e) => setCompany(e.target.value)} className="text-sm rounded-lg border border-slate-300 bg-white px-2 py-2">
          <option value="all">All companies</option>
          {companies.map(([code, name]) => (
            <option key={code} value={code}>{name}</option>
          ))}
        </select>
        <select id="fleet-platform" aria-label="Platform" value={platform} onChange={(e) => setPlatform(e.target.value as 'all' | Platform)} className="text-sm rounded-lg border border-slate-300 bg-white px-2 py-2">
          <option value="all">iOS and Android</option>
          <option value="ios">iOS</option>
          <option value="android">Android</option>
        </select>
        <select id="fleet-channel" aria-label="Channel" value={channel} onChange={(e) => setChannel(e.target.value)} className="text-sm rounded-lg border border-slate-300 bg-white px-2 py-2">
          <option value="all">All channels</option>
          {channels.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select id="fleet-active" aria-label="Activity" value={active} onChange={(e) => setActive(e.target.value as '7' | '30' | 'all')} className="text-sm rounded-lg border border-slate-300 bg-white px-2 py-2">
          <option value="30">Seen in last 30 days</option>
          <option value="7">Seen in last 7 days</option>
          <option value="all">Any time (incl. inactive)</option>
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {(
          [
            ['all', 'All'],
            ['latest', 'Up to date'],
            ['available', 'Update available'],
            ['no_updates', 'No updates for build'],
            ['store', 'Needs store update'],
            ['unrecorded', 'Not recorded'],
            ['unknown', "Old app"],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            aria-pressed={status === k}
            onClick={() => setStatus(k)}
            className={`text-xs px-3 py-1 rounded-full border ${
              status === k ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {label}
          </button>
        ))}
        {retiredCount > 0 || showRetired ? (
          <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-500">
            <input id="fleet-show-retired" type="checkbox" checked={showRetired} onChange={(e) => onShowRetired(e.target.checked)} />
            Show retired and merged{retiredCount ? ` (${retiredCount})` : ''}
          </label>
        ) : null}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400">
            <tr>
              <th className="text-left font-semibold px-3 py-2.5">Employee</th>
              <th className="text-left font-semibold px-3 py-2.5">Device</th>
              <th className="text-left font-semibold px-3 py-2.5">Running</th>
              <th className="text-left font-semibold px-3 py-2.5">Status</th>
              <th className="text-left font-semibold px-3 py-2.5">Channel</th>
              <th className="text-left font-semibold px-3 py-2.5">Last check-in</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const s = d.status
              const stale = daysSince(d.last_seen_at) > 14
              return (
                <tr
                  key={d.id}
                  tabIndex={0}
                  onClick={() => onOpen(d)}
                  onKeyDown={(e) => e.key === 'Enter' && onOpen(d)}
                  className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 focus:outline-2 focus:outline-blue-500 ${d.retired_at || d.inactive ? 'opacity-60' : ''}`}
                >
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-slate-900">
                      {deviceName(d)}
                      {d.superseded_by ? (
                        <span className="ml-2 text-[11px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Merged</span>
                      ) : d.retired_at ? (
                        <span className="ml-2 text-[11px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Retired</span>
                      ) : d.inactive ? (
                        <span className="ml-2 text-[11px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Inactive</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-slate-400">
                      {d.employee_id && d.employee_name ? `${d.employee_id} · ` : ''}
                      {d.company_name || d.company_code}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5 text-slate-800">
                      <DevicePhoneMobileIcon className="w-4 h-4 text-slate-400" />
                      {d.device_model || platformLabel(d.platform)}
                    </div>
                    <div className="text-xs text-slate-400">
                      {platformLabel(d.platform)}
                      {d.os_version ? ` ${d.os_version}` : ''}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-mono text-[13px] text-slate-900">
                      <span className="text-slate-500">{storeLabel(d)}</span>
                      <span className="text-slate-300"> · </span>
                      <span className="font-semibold">{otaLabel(d)}</span>
                    </div>
                    <Fingerprint value={d.runtime_version} className="text-[11px] text-slate-400" />
                  </td>
                  <td className="px-3 py-2.5">
                    <span title={s.detail} className={`inline-flex px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${STATUS_STYLE[s.key]}`}>{s.label}</span>
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">{channelOf(d)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className={stale ? 'text-amber-700' : 'text-slate-800'}>{timeAgo(d.last_seen_at)}</div>
                    <div className="text-xs text-slate-400">{formatWhen(d.last_seen_at)}</div>
                  </td>
                </tr>
              )
            })}
            {!rows.length && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-slate-400">
                  {loading ? 'Loading devices…' : devices.length ? 'No devices match these filters.' : 'No devices have checked in yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400 mt-2">
        Showing {rows.length} of {live.length} devices. Retired and merged devices, and ones outside the activity window, are hidden.
      </p>
    </>
  )
}

function PlatformCard({ platform, devices, latest, builds }: { platform: Platform; devices: Device[]; latest: OtaRelease[]; builds: NativeBuild[] }) {
  const seg = Object.fromEntries(STATUS_ORDER.map(([k]) => [k, 0])) as Record<StatusKey, number>
  for (const d of devices) seg[d.status.key]++
  const n = devices.length || 1
  const upToDate = seg.latest + seg.no_updates
  // Newest release per runtime, production channel first; only runtimes a
  // phone actually runs (an update to a fingerprint nobody has is a health issue, not "latest").
  const inUse = new Set(devices.map((d) => d.runtime_version).filter(Boolean) as string[])
  const rows = latest
    .filter((r) => r.runtime_version && inUse.has(r.runtime_version))
    .sort((a, b) => (a.channel === 'prod' ? -1 : 0) - (b.channel === 'prod' ? -1 : 0) || new Date(b.published_at).getTime() - new Date(a.published_at).getTime())
    .slice(0, 4)
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-slate-900">{platformLabel(platform)}</span>
        <span className="text-xs font-medium px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">
          {upToDate} of {devices.length} current
        </span>
      </div>
      <div className="space-y-1">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Latest update per build</div>
        {rows.length ? (
          rows.map((r) => {
            const bs = buildsForRuntime(builds, platform, r.runtime_version)
            return (
              <div key={`${r.runtime_version}-${r.channel}`} className="flex flex-wrap items-center gap-x-2 text-sm">
                <span className="font-mono font-semibold text-slate-900">{updateName(r)}</span>
                <span className="text-slate-400">{r.channel}</span>
                <span className="text-slate-500">{bs.length ? `store ${bs.map(buildLabel).join(', ')}` : 'store build unknown'}</span>
                <Fingerprint value={r.runtime_version} className="text-[11px] text-slate-400" />
              </div>
            )
          })
        ) : (
          <div className="text-sm text-slate-500">No recorded update for the builds phones run.</div>
        )}
      </div>
      <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100" role="img" aria-label="Status spread">
        {STATUS_ORDER.map(([k]) => (
          <span key={k} className={SEGMENT_COLOR[k]} style={{ width: `${(seg[k] / n) * 100}%` }} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
        {STATUS_ORDER.filter(([k]) => seg[k] > 0).map(([k, label]) => (
          <span key={k} className="inline-flex items-center gap-1">
            <i className={`inline-block w-2 h-2 rounded-sm ${SEGMENT_COLOR[k]}`} />
            {label} {seg[k]}
          </span>
        ))}
        {!devices.length && <span>No active devices</span>}
      </div>
    </div>
  )
}

function exportCsv(devices: Device[]) {
  const header = ['Employee', 'Employee ID', 'Company', 'Platform', 'OS', 'Model', 'Version', 'Store version', 'Native build', 'OTA version', 'Built-in bundle', 'Runtime', 'Update ID', 'Status', 'Channel', 'Inactive', 'Last check-in', 'Device ID']
  const lines = devices.map((d) =>
    [
      d.employee_name ?? '', d.employee_id ?? '', d.company_name || d.company_code, platformLabel(d.platform), d.os_version ?? '',
      d.device_model ?? '', versionLine(d), d.app_version ?? '', d.native_build ?? '', d.ota_version ?? '',
      d.status.embedded == null ? '' : d.status.embedded ? 'yes' : 'no', d.runtime_version ?? '', d.update_id ?? '', d.status.label,
      channelOf(d), d.inactive ? 'yes' : '', d.last_seen_at, d.id,
    ].map(csvCell).join(',')
  )
  const blob = new Blob(['﻿' + [header.join(','), ...lines].join('\r\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `app-fleet-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

/* ── Releases ────────────────────────────────────────────────────────────── */

interface RuntimeGroup {
  key: string
  platform: Platform
  runtime: string | null
  legacy: boolean
  releases: OtaRelease[]
}

function ReleasesTab({ ota, builds, devices }: { ota: OtaRelease[]; builds: NativeBuild[]; devices: Device[] }) {
  const groups = useMemo(() => {
    const m = new Map<string, RuntimeGroup>()
    for (const r of ota) {
      const legacy = !r.runtime_version
      const runtime = r.runtime_version || r.app_version
      const key = `${r.platform}|${runtime}`
      if (!m.has(key)) m.set(key, { key, platform: r.platform, runtime, legacy, releases: [] })
      m.get(key)!.releases.push(r)
    }
    const list = [...m.values()]
    for (const g of list) g.releases.sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime())
    return list.sort(
      (a, b) =>
        a.platform.localeCompare(b.platform) ||
        Number(a.legacy) - Number(b.legacy) ||
        new Date(b.releases[0].published_at).getTime() - new Date(a.releases[0].published_at).getTime()
    )
  }, [ota])

  const onRelease = useMemo(() => {
    const m = new Map<number, number>()
    for (const d of devices) {
      if (d.inactive || d.status.release_id == null) continue
      m.set(d.status.release_id, (m.get(d.status.release_id) || 0) + 1)
    }
    return m
  }, [devices])

  if (!groups.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-10 text-center text-sm text-slate-400">
        No OTA updates recorded yet. They appear after the next publish (with CITS_INTERNAL_API_KEY set), or record one with
        RN/scripts/record-release.mjs.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        An update only reaches phones whose native build has the same runtime fingerprint, so releases are grouped by platform and
        fingerprint. Device counts include active phones only.
      </p>
      {groups.map((g) => {
        const bs = g.legacy ? [] : buildsForRuntime(builds, g.platform, g.runtime)
        const onRuntime = devices.filter((d) => !d.inactive && d.platform === g.platform && d.runtime_version === g.runtime)
        return (
          <section key={g.key} className="bg-white border border-slate-200 rounded-xl">
            <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <h3 className="font-semibold text-slate-900">{platformLabel(g.platform)}</h3>
                {g.legacy ? (
                  <span className="text-sm text-slate-500">store {g.runtime} (before EAS: runtime was the app version)</span>
                ) : (
                  <>
                    <Fingerprint value={g.runtime} className="text-sm text-slate-700" />
                    <span className="text-sm text-slate-500">
                      {bs.length ? `Store build${bs.length > 1 ? 's' : ''} ${bs.map(buildLabel).join(', ')}` : 'No phone has reported this build'}
                    </span>
                  </>
                )}
              </div>
              <span className="text-xs text-slate-500">{onRuntime.length} active phone{onRuntime.length === 1 ? '' : 's'} on this runtime</span>
            </div>
            {!g.legacy && !bs.length && (
              <div className="flex items-center gap-2 px-4 py-2 text-xs text-amber-800 bg-amber-50 border-b border-amber-100">
                <ExclamationTriangleIcon className="w-4 h-4" /> Nothing can receive these updates until a store build with this fingerprint is installed.
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="text-left font-semibold px-3 py-2">Update</th>
                    <th className="text-left font-semibold px-3 py-2">Channel</th>
                    <th className="text-left font-semibold px-3 py-2">Published</th>
                    <th className="text-left font-semibold px-3 py-2">What changed</th>
                    <th className="text-left font-semibold px-3 py-2">Phones on it</th>
                  </tr>
                </thead>
                <tbody>
                  {g.releases.map((r, i) => {
                    const on = r.id != null ? onRelease.get(r.id) || 0 : 0
                    const pool = onRuntime.filter((d) => channelOf(d) === r.channel || (!r.channel && channelOf(d) === 'prod')).length
                    const pct = pool ? Math.round((on / pool) * 100) : 0
                    return (
                      <tr key={`${r.id ?? i}-${r.ota_build}-${r.channel}`} className="border-t border-slate-100">
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-slate-900">{updateName(r)}</span>
                            {r.update_level && r.update_level !== 'normal' && (
                              <span className={`text-[10px] px-1.5 rounded ${LEVEL_STYLE[r.update_level]}`}>{r.update_level}</span>
                            )}
                          </div>
                          {r.eas_update_id && <div className="font-mono text-[11px] text-slate-400" title={r.eas_update_id}>{r.eas_update_id.slice(0, 8)}</div>}
                        </td>
                        <td className="px-3 py-2 text-slate-700">{r.channel}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                          {formatWhen(r.published_at)}
                          {r.source === 'reported' && <div className="text-[11px] text-slate-400">first seen on devices</div>}
                        </td>
                        <td className="px-3 py-2 text-slate-700">
                          {r.message || <span className="text-slate-400">—</span>}
                          {r.git_commit && <span className="ml-1.5 font-mono text-[11px] text-slate-400">{r.git_commit}</span>}
                        </td>
                        <td className="px-3 py-2">
                          {g.legacy ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : (
                            <div className="flex items-center gap-2 min-w-[150px]">
                              <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                <div className={`h-full ${i === 0 ? 'bg-blue-600' : 'bg-slate-400'}`} style={{ width: `${pct}%` }} />
                              </div>
                              <span className="font-mono text-xs text-slate-600">{on}/{pool}</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )
      })}
    </div>
  )
}

/* ── Builds ──────────────────────────────────────────────────────────────── */

function BuildsTab({ builds }: { builds: NativeBuild[] }) {
  const sorted = [...builds].sort(
    (a, b) =>
      a.platform.localeCompare(b.platform) ||
      compareVersions(b.store_version, a.store_version) ||
      Number(b.native_build || 0) - Number(a.native_build || 0) ||
      new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime()
  )
  return (
    <section className="bg-white border border-slate-200 rounded-xl">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap justify-between gap-2">
        <h3 className="font-semibold text-slate-900">Native builds</h3>
        <span className="text-xs text-slate-400">
          Every store build phones have reported, with its runtime fingerprint. An OTA reaches a build only if published for its fingerprint.
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400">
            <tr>
              <th className="text-left font-semibold px-3 py-2.5">Platform</th>
              <th className="text-left font-semibold px-3 py-2.5">Store version (build)</th>
              <th className="text-left font-semibold px-3 py-2.5">Fingerprint</th>
              <th className="text-left font-semibold px-3 py-2.5">Channel</th>
              <th className="text-left font-semibold px-3 py-2.5">First seen</th>
              <th className="text-left font-semibold px-3 py-2.5">Last seen</th>
              <th className="text-left font-semibold px-3 py-2.5">Active phones</th>
              <th className="text-left font-semibold px-3 py-2.5">Latest update</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((b) => (
              <tr key={`${b.platform}-${b.store_version}-${b.native_build}-${b.runtime_version}`} className={`border-t border-slate-100 ${b.active_devices ? '' : 'opacity-60'}`}>
                <td className="px-3 py-2.5 text-slate-700">{platformLabel(b.platform)}</td>
                <td className="px-3 py-2.5 font-mono text-slate-900">
                  {buildLabel(b)}
                  {b.label && <div className="font-sans text-xs text-slate-500">{b.label}</div>}
                </td>
                <td className="px-3 py-2.5">
                  <Fingerprint value={b.runtime_version} partial={b.is_partial} className="text-slate-700" />
                </td>
                <td className="px-3 py-2.5 text-slate-700">{b.channel || '—'}</td>
                <td className="px-3 py-2.5 whitespace-nowrap text-slate-700">{formatWhen(b.first_seen_at)}</td>
                <td className="px-3 py-2.5 whitespace-nowrap text-slate-700">{timeAgo(b.last_seen_at)}</td>
                <td className="px-3 py-2.5 font-mono text-slate-700">
                  {b.active_devices}
                  {b.devices > b.active_devices && <span className="text-slate-400"> / {b.devices}</span>}
                </td>
                <td className="px-3 py-2.5 text-slate-700">
                  {b.latest_release ? (
                    <>
                      <span className="font-mono font-semibold">{updateName(b.latest_release)}</span>
                      <span className="text-xs text-slate-400"> {b.latest_release.channel} · {formatWhen(b.latest_release.published_at)}</span>
                    </>
                  ) : (
                    <span className="text-slate-400">No updates published for this build</span>
                  )}
                </td>
              </tr>
            ))}
            {!sorted.length && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-slate-400">
                  No native builds known yet. They register themselves as phones check in (after the 2026-9-28 app-fleet-eas migration).
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {builds.some((b) => b.derived) && (
        <p className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100">
          Derived from device check-ins: the app_native_builds table isn't on this registry yet (run migrations-2026-9-28-app-fleet-eas).
        </p>
      )}
    </section>
  )
}

/* ── Update policy ───────────────────────────────────────────────────────── */

function PolicyTab({ devices, notify }: { devices: Device[]; notify: (m: string, t?: 'ok' | 'error') => void }) {
  const [form, setForm] = useState<PolicyForm>(EMPTY_POLICY)
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    api
      .get('/super-admin/global-settings')
      .then((r) => {
        const s = r.data.settings || {}
        const v = (k: string) => (s[k]?.value == null ? '' : String(s[k].value))
        setForm({
          force_update_enabled: v('force_update_enabled') === 'true' || v('force_update_enabled') === '1',
          min_ios_version: v('min_ios_version'),
          min_android_version: v('min_android_version'),
          min_pwa_version: v('min_pwa_version'),
          update_message: v('update_message'),
          ios_store_url: v('ios_store_url'),
          android_store_url: v('android_store_url'),
        })
      })
      .catch((err) => notify(errMsg(err, 'Failed to load the update policy'), 'error'))
      .finally(() => setLoaded(true))
  }, [notify])

  // Newest store version phones report, as the placeholder for a minimum.
  const newestStore = (p: Platform) =>
    devices.filter((d) => d.platform === p && d.app_version).map((d) => d.app_version as string).sort((a, b) => compareVersions(b, a))[0] ?? null

  const set = <K extends keyof PolicyForm>(k: K, v: PolicyForm[K]) => setForm((f) => ({ ...f, [k]: v }))
  const valid = isVersion(form.min_ios_version) && isVersion(form.min_android_version) && isVersion(form.min_pwa_version)

  const save = async () => {
    setSaving(true)
    try {
      await Promise.all(
        (Object.keys(form) as Array<keyof PolicyForm>).map((k) =>
          api.put(`/super-admin/global-settings/${k}`, { value: typeof form[k] === 'boolean' ? String(form[k]) : String(form[k]).trim() })
        )
      )
      notify('Update policy saved')
    } catch (err) {
      notify(errMsg(err, 'Failed to save the update policy'), 'error')
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return <p className="text-sm text-slate-400">Loading update policy…</p>

  return (
    <div className="max-w-3xl space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-4 flex items-center justify-between gap-4">
        <div>
          <div className="font-semibold text-slate-900">Force update</div>
          <p className="text-sm text-slate-500">
            When on, phones with a store version below the minimum can't be used until they install the latest version from
            the App Store or Play Store. OTA updates don't count toward the minimum; they are applied by their own level
            (critical updates show a restart screen).
          </p>
        </div>
        <button
          role="switch"
          aria-checked={form.force_update_enabled}
          aria-label="Force update"
          onClick={() => set('force_update_enabled', !form.force_update_enabled)}
          className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors ${form.force_update_enabled ? 'bg-red-600' : 'bg-slate-300'}`}
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${form.force_update_enabled ? 'left-[22px]' : 'left-0.5'}`} />
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <div>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Minimum version</h3>
          <p className="text-sm text-slate-500 mt-1">
            The minimum App Store / Play Store version. A phone on store 4.0.28 is below a 4.0.29 minimum even if an OTA makes it
            read 4.0.33.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {(['ios', 'android'] as Platform[]).map((p) => {
            const key = p === 'ios' ? 'min_ios_version' : 'min_android_version'
            return (
              <div key={p} className="space-y-1.5">
                <label htmlFor={`policy-${key}`} className="text-sm font-semibold text-slate-800">{platformLabel(p)}</label>
                <input
                  id={`policy-${key}`}
                  value={form[key]}
                  onChange={(e) => set(key, e.target.value)}
                  placeholder={newestStore(p) ?? '4.0.0'}
                  className={`w-full font-mono text-sm rounded-lg border px-3 py-2 ${isVersion(form[key]) ? 'border-slate-300' : 'border-red-400'}`}
                />
                <Impact platform={p} min={form[key]} devices={devices} />
              </div>
            )
          })}
          <div className="space-y-1.5">
            <label htmlFor="policy-min_pwa_version" className="text-sm font-semibold text-slate-800">Web app</label>
            <input
              id="policy-min_pwa_version"
              value={form.min_pwa_version}
              onChange={(e) => set('min_pwa_version', e.target.value)}
              className={`w-full font-mono text-sm rounded-lg border px-3 py-2 ${isVersion(form.min_pwa_version) ? 'border-slate-300' : 'border-red-400'}`}
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2 space-y-1.5">
          <label htmlFor="policy-message" className="text-sm font-semibold text-slate-800">Message shown to employees</label>
          <textarea
            id="policy-message"
            value={form.update_message}
            onChange={(e) => set('update_message', e.target.value)}
            rows={3}
            className="w-full text-sm rounded-lg border border-slate-300 px-3 py-2"
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="policy-ios-url" className="text-sm font-semibold text-slate-800">App Store link</label>
          <input id="policy-ios-url" value={form.ios_store_url} onChange={(e) => set('ios_store_url', e.target.value)} className="w-full text-sm rounded-lg border border-slate-300 px-3 py-2" />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="policy-android-url" className="text-sm font-semibold text-slate-800">Play Store link</label>
          <input id="policy-android-url" value={form.android_store_url} onChange={(e) => set('android_store_url', e.target.value)} className="w-full text-sm rounded-lg border border-slate-300 px-3 py-2" />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={!valid || saving}
          className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save policy'}
        </button>
        {!valid && <span className="text-sm text-red-600">Versions must look like 4.0.12</span>}
      </div>
    </div>
  )
}

function Impact({ platform, min, devices }: { platform: Platform; min: string; devices: Device[] }) {
  if (!isVersion(min)) return <p className="text-xs text-red-600">Use a version like 4.0.12</p>
  const pool = devices.filter((d) => d.platform === platform && !d.inactive)
  // Store minimum: the store version installed decides, never the OTA on top of it.
  const below = pool.filter((d) => d.app_version && compareVersions(d.app_version, min) < 0)
  const unknown = pool.filter((d) => !d.app_version).length
  return (
    <p className="text-xs rounded-md bg-slate-50 px-2.5 py-1.5 text-slate-600">
      {below.length ? (
        <>
          <b className="text-red-700">{below.length} of {pool.length}</b> active phones have a store version below {min} and will be
          sent to the store.
        </>
      ) : (
        <span className="inline-flex items-center gap-1">
          <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-600" /> All {pool.length - unknown} reporting phones are on store {min} or newer.
        </span>
      )}
      {unknown > 0 && ` ${unknown} haven't reported a store version yet.`}
    </p>
  )
}
